import { FunctionComponent } from "react";

import { classNames } from "@/utils/classNames";
import { SidenavItem } from "@/constants/sidenav.config";

import { SidebarItem } from "../SidebarItem/SidebarItem";
import { SidebarGroup } from "../SidebarItem/SidebarGroup";
import { filterNavItems, NavEnrichResult } from "./filterNavItems";

interface SidebarSectionProps {
	label: string;
	items: SidenavItem[];
	isExpanded: boolean;
	filterItem: (item: SidenavItem) => boolean;
	enrich?: (item: SidenavItem) => NavEnrichResult;
}

/**
 * Renders one labelled nav group: applies the shared permission/feature/company
 * gate (descending into expandable groups), hides itself when nothing is
 * visible, and swaps the section label for a divider line while collapsed
 * (nav-v7 design).
 */
export const SidebarSection: FunctionComponent<SidebarSectionProps> = ({
	label,
	items,
	isExpanded,
	filterItem,
	enrich,
}) => {
	const visibleItems = filterNavItems(items, filterItem);

	if (visibleItems.length === 0) {
		return null;
	}

	return (
		<div className="mt-3.5 first:mt-0.5">
			{isExpanded ? (
				<div className="px-[10px] pb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-sidebar-section whitespace-nowrap overflow-hidden">
					{label}
				</div>
			) : (
				<div className="mx-[14px] my-2 h-px bg-gray-200" aria-hidden />
			)}
			<div className={classNames("flex flex-col", isExpanded ? "items-stretch" : "items-center")}>
				{visibleItems.map((item) =>
					item.subItems ? (
						<SidebarGroup
							key={item.id ?? item.title}
							enrich={enrich}
							isSidebarOpen={isExpanded}
							item={item}
						/>
					) : (
						<SidebarItem
							key={item.path ?? item.title}
							{...item}
							{...(enrich?.(item) ?? {})}
							isSidebarOpen={isExpanded}
						/>
					)
				)}
			</div>
		</div>
	);
};
